/**
 * CypAdhdPatientOverview.js
 *
 * Provider-facing Patient Overview for CYP ADHD patients. Mirrors the Adult
 * Autism overview layout but adapted for CYP ADHD with four stages (no
 * Behavioural Observation) and a dual-column CI (parent/school) instead of
 * the adult history prefill.
 *
 * Assessment module order (four blocks — CI and SSI happen in one appointment
 * but are separate blocks; see CYP_ADHD_MODULE_CONFIG below, which is the
 * source of truth this comment must not drift from):
 *   1. Clinical Interview
 *   2. Semi-Structured Interview
 *   3. Diagnostic Decision
 *   4. Post-Assessment Discussion
 *
 * Reads from ModularPatientDataContext which fetches from:
 *   patients/{id}/cyp_adhd/assessment_001/assessmentStages/{stageKey}
 *   patients/{id}/cyp_adhd/assessment_001/reportContent/{stageKey}
 *   informants/{uid}/patients/{id}/cyp_adhd/assessment_001/formInstances/{formKey}
 * Patient-side formInstances are not used: the child has no questionnaires.
 */
import React, { useEffect, useContext, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import { ModularPatientDataContext } from '../../../../context/ModularPatientDataContext';
import PatientInfoPanel from '../components/PatientInfoPanel';
import Breadcrumb from '../../../../components/Breadcrumb';
import AssessmentModuleCard from '../components/AssessmentModuleCard';
import CypAdhdPreAppointmentDashboard from './CypAdhdPreAppointmentDashboard';
import useAnalytics from '../../../../hooks/useAnalytics';
import { useAuth } from '../../../../hooks/useAuth';
import { getAssessmentModuleCardWidthClass } from '../utils/getAssessmentModuleCardWidthClass';
import AssignCliniciansDialog from '../components/AssignCliniciansDialog';
import { buildModularPatientOverviewModel, buildFormDataMapFromInstances } from '../utils/buildModularPatientOverviewModel';
import { resolveFormInstanceForDisplay } from '../../../../utils/resolveFormTemplates';
import { fetchEnrichedInformantData } from '../../../../utils/informantFirestorePaths';
import { getReportApprovedField, getReportGeneratedField, STAGE_TO_PROGRESS_KEY } from '../../../../constants/cypAdhdStages';
import AppointmentDateDialog from '../components/AppointmentDateDialog';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { resolveDisplayAppointmentDate } from '../../../../utils/appointmentDateHelpers';
import { appendPreviewQuery, canStartAppointment } from '../../../../utils/appointmentStageRouting';
import {
  predictCypAdhdAppointmentDateReportRefinalisation,
  rerenderAfterCypAdhdAppointmentDateChange,
} from '../../../../utils/rerenderAfterCypAdhdAppointmentDateChange';
import { CYP_ADHD_MODULE_CONFIG } from './config/cypAdhdModuleConfig';

function CypAdhdPatientOverview() {
  const { patientId } = useParams();

  const {
    header,
    formInstances,
    assessmentStages,
    reportContent,
    fetchPatientData,
  } = useContext(ModularPatientDataContext);

  const patientDataModel = useMemo(
    () => buildModularPatientOverviewModel({ header, formInstances, assessmentStages }),
    [header, formInstances, assessmentStages],
  );

  const [CIProgress, setCIProgress] = useState(0);
  const [SSIProgress, setSSIProgress] = useState(0);
  const [DDProgress, setDDProgress] = useState(0);
  const [PADProgress, setPADProgress] = useState(0);
  const [informantData, setInformantData] = useState({});
  const { trackEvent } = useAnalytics();
  const { claims, user } = useAuth();
  // Workflow config resolved for isAppointmentDatesEnabled — the org flag is
  // on for WLC V2, and an adult patient there shows appointment dates on every
  // card while a CYP patient in the same org showed none (audit, 26 Aug) —
  // and for cypEnableMdtSupportingClinician (the add-MDT-clinician action on
  // the Diagnostic Decision card; TOC, TDI-208). The stage layout itself stays
  // fixed (ASMT-14).
  const wf = useWorkflowConfig(header, claims);

  // Per-module appointment dates, adult-parity. The stored shape is the same
  // (patients/{id}.appointmentDates.{ci|ssi|dd|pad} + the appointmentDateHistory
  // audit subcollection — both pathway-agnostic); the inference source is
  // CYP's: completion lives on assessmentStages.{stageKey}, not on a
  // dataModel this patient does not have. The short keys are the same set the
  // dashboard pills use (STAGE_TO_PROGRESS_KEY: ci/ssi/dd/pad).
  const [appointmentDateDialogModule, setAppointmentDateDialogModule] = useState(null);
  const resolveModuleAppointmentDate = useCallback((moduleKey) => {
    const shortKey = STAGE_TO_PROGRESS_KEY[moduleKey];
    const stageDoc = assessmentStages?.[moduleKey];
    return {
      shortKey,
      ...resolveDisplayAppointmentDate({
        appointmentDate: header?.appointmentDates?.[shortKey]?.date,
        firstCompletedAt: stageDoc?.firstCompletedAt,
        lastCompletedAt: stageDoc?.lastCompletedAt,
      }),
    };
  }, [assessmentStages, header?.appointmentDates]);

  const config = CYP_ADHD_MODULE_CONFIG;

  const subcollectionAccess = useMemo(() => ({
    organisation: patientDataModel?.organisation || claims?.organisation || '',
    clinicianUid: user?.uid || '',
    isOrgWideAccess: claims?.userType === 'Org. Admin' || claims?.userType === 'Support',
  }), [patientDataModel?.organisation, claims?.organisation, claims?.userType, user?.uid]);

  const [requestReassignStep, setRequestReassignStep] = useState(null);
  const [showAssignLeadDialog, setShowAssignLeadDialog] = useState(false);

  useEffect(() => {
    if (patientId) {
      trackEvent('cyp_adhd_patient_overview_viewed', {
        patientId,
        timestamp: new Date().toISOString(),
      });
    }
  }, [patientId, trackEvent]);

  // Fetch enriched informant data for the pre-assessment dashboard.
  // CYP ADHD informants are guardians and school reps who complete
  // questionnaires on behalf of the child.
  useEffect(() => {
    let isCurrent = true;
    setInformantData({});

    const fetchInformants = async () => {
      if (!patientDataModel?.informants || Object.keys(patientDataModel.informants).length === 0) {
        return;
      }

      const nextInformantsData = {};
      const informantIds = Object.keys(patientDataModel.informants);

      for (const informantId of informantIds) {
        try {
          const mapEntry = patientDataModel.informants[informantId];
          const enriched = await fetchEnrichedInformantData(
            informantId,
            patientId,
            mapEntry,
            subcollectionAccess,
          );
          if (!isCurrent) return;
          if (enriched) {
            const displayName = mapEntry?.informantName
              || [enriched.firstName, enriched.lastName].filter(Boolean).join(' ')
              || enriched.email
              || (mapEntry?.informantRole === 'schoolRep'
                ? 'School representative' : 'Parent/carer');

            const patientFirstName = patientDataModel?.firstName || '';
            const resolvedFormInstances = enriched.isAutismInformant
              ? Object.fromEntries(
                Object.entries(enriched.formInstances || {}).map(([key, fi]) => [
                  key,
                  resolveFormInstanceForDisplay(fi, patientFirstName),
                ]),
              )
              : enriched.formInstances;

            nextInformantsData[informantId] = {
              ...enriched,
              displayName,
              // The resolved instances must replace the raw ones, not just feed
              // formData. InformantHistoryPanel builds its form list from
              // informantViewData.formInstances, so leaving the spread's raw
              // copy in place showed clinicians the literal
              // "{{patientFirstName}}" in every CYP form title — 11 of the
              // guardian/school titles carry the token, where no adult autism
              // title does, which is why the same omission was invisible there.
              formInstances: resolvedFormInstances,
              formData: enriched.isAutismInformant
                ? buildFormDataMapFromInstances(resolvedFormInstances)
                : enriched.formData,
            };
          }
        } catch (error) {
          console.error(`Error fetching informant data for ${informantId}:`, error);
        }
      }

      if (isCurrent) {
        setInformantData(nextInformantsData);
      }
    };

    fetchInformants();
    return () => { isCurrent = false; };
  }, [patientId, patientDataModel?.informants, patientDataModel?.firstName, subcollectionAccess]);

  // Compute per-stage progress from the modular assessmentStages docs.
  // Progress is persisted to each stage doc by the proforma pages and
  // read back here to drive the dashboard pills.
  useEffect(() => {
    if (!patientDataModel) return;

    const isStarted = (status) => status === 'inProgress' || status === 'completed';

    // CI progress
    const ciStage = assessmentStages?.clinicalInterview;
    let ciProgressValue;
    if (ciStage?.status === 'completed') {
      ciProgressValue = 100;
    } else if (!isStarted(ciStage?.status)) {
      ciProgressValue = 0;
    } else if (typeof ciStage?.progress === 'number') {
      ciProgressValue = Math.round(ciStage.progress * 100);
    } else {
      ciProgressValue = 0;
    }
    setCIProgress(ciProgressValue);

    // SSI progress
    const ssiStage = assessmentStages?.semiStructuredInterview;
    let ssiProgressValue;
    if (ssiStage?.status === 'completed') {
      ssiProgressValue = 100;
    } else if (!isStarted(ssiStage?.status)) {
      ssiProgressValue = 0;
    } else if (typeof ssiStage?.progress === 'number') {
      ssiProgressValue = Math.round(ssiStage.progress * 100);
    } else {
      ssiProgressValue = 0;
    }
    setSSIProgress(ssiProgressValue);

    // DD progress
    const ddStage = assessmentStages?.diagnosticDecision;
    let ddProgressValue;
    if (ddStage?.status === 'completed') {
      ddProgressValue = 100;
    } else if (!isStarted(ddStage?.status)) {
      ddProgressValue = 0;
    } else if (typeof ddStage?.progress === 'number') {
      ddProgressValue = Math.round(ddStage.progress * 100);
    } else {
      ddProgressValue = 0;
    }
    setDDProgress(ddProgressValue);

    // PAD progress
    const padStage = assessmentStages?.postAssessmentDiscussion;
    let padProgressValue;
    if (padStage?.status === 'completed') {
      padProgressValue = 100;
    } else if (!isStarted(padStage?.status)) {
      padProgressValue = 0;
    } else if (typeof padStage?.progress === 'number') {
      padProgressValue = Math.round(padStage.progress * 100);
    } else {
      padProgressValue = 0;
    }
    setPADProgress(padProgressValue);
  }, [patientDataModel, assessmentStages]);

  // DD lock: both CI and SSI reports must be approved before DD unlocks.
  // Also unlocks if DD was previously completed (lastCompletedAt bypass).
  // DD unlocks on the CI report approval, plus the SSI report approval ONCE
  // THE SSI STAGE EXISTS. The decided rule is CI + SSI together, but the SSI
  // stage is not built yet, so nothing can write ssiReportApproved — requiring
  // it unconditionally left DD permanently locked for every CYP patient
  // (audit, 25 Aug). Keying it on the SSI report document existing reinstates
  // the full rule automatically when the stage ships. Must stay in step with
  // compute_cyp_adhd_progress_by_stage in cyp_adhd_stage_progress.py, which
  // computes the dashboard pill from the same inputs.
  const isDDLocked = useMemo(() => {
    const ddStage = assessmentStages?.diagnosticDecision || {};
    if (ddStage.lastCompletedAt) return false;
    const ciReport = reportContent?.clinicalInterview || {};
    const ssiReport = reportContent?.semiStructuredInterview;
    const ssiStageExists = Boolean(ssiReport && Object.keys(ssiReport).length > 0);
    const ssiSatisfied = ssiReport?.ssiReportApproved === true || !ssiStageExists;
    return !(ciReport.ciReportApproved === true && ssiSatisfied);
  }, [assessmentStages, reportContent]);

  // PAD lock: requires DD completed
  const isPADLocked = useMemo(() => {
    const ddStage = assessmentStages?.diagnosticDecision || {};
    return ddStage.status !== 'completed';
  }, [assessmentStages]);

  const isModuleLocked = useCallback((moduleKey) => {
    if (moduleKey === 'diagnosticDecision') return isDDLocked;
    if (moduleKey === 'postAssessmentDiscussion') return isPADLocked;
    return false;
  }, [isDDLocked, isPADLocked]);

  // The tooltip must only ever name work the clinician can actually do. While
  // the SSI stage does not exist, asking them to approve its report is an
  // instruction they cannot follow, so the message names the Clinical
  // Interview alone until an SSI report is in play.
  const getModuleLockedTooltip = useCallback((moduleKey) => {
    if (moduleKey === 'diagnosticDecision' && isDDLocked) {
      const ssiReport = reportContent?.semiStructuredInterview;
      const ssiStageExists = Boolean(ssiReport && Object.keys(ssiReport).length > 0);
      return ssiStageExists
        ? 'Complete and approve the Clinical Interview and Semi-Structured Interview reports before starting the Diagnostic Decision.'
        : 'Complete and approve the Clinical Interview report before starting the Diagnostic Decision.';
    }
    if (moduleKey === 'postAssessmentDiscussion' && isPADLocked) {
      return 'Complete the Diagnostic Decision before starting the Post-Assessment Discussion.';
    }
    return '';
  }, [isDDLocked, isPADLocked, reportContent]);

  const renderModuleCard = (moduleKey, progress) => {
    const moduleConfig = config.modules[moduleKey];
    if (!moduleConfig) return null;

    const isLocked = isModuleLocked(moduleKey);

    // Report availability checks for the CI, SSI, DD and PAD review buttons
    const ciReportDoc = reportContent?.clinicalInterview;
    const ciDocsAvailable = Boolean(
      ciReportDoc?.[getReportGeneratedField('clinicalInterview')]
      || ciReportDoc?.[getReportApprovedField('clinicalInterview')]
      || (ciReportDoc?.outputs && Object.keys(ciReportDoc.outputs).length > 0),
    );

    const ssiReportDoc = reportContent?.semiStructuredInterview;
    const ssiDocsAvailable = Boolean(
      ssiReportDoc?.[getReportGeneratedField('semiStructuredInterview')]
      || ssiReportDoc?.[getReportApprovedField('semiStructuredInterview')]
      || (ssiReportDoc?.outputs && Object.keys(ssiReportDoc.outputs).length > 0),
    );

    const ddReportDoc = reportContent?.diagnosticDecision;
    const ddDocsAvailable = Boolean(
      ddReportDoc?.[getReportGeneratedField('diagnosticDecision')]
      || ddReportDoc?.[getReportApprovedField('diagnosticDecision')]
      || (ddReportDoc?.outputs && Object.keys(ddReportDoc.outputs).length > 0),
    );

    const padReportDoc = reportContent?.postAssessmentDiscussion;
    const padDocsAvailable = Boolean(
      padReportDoc?.[getReportGeneratedField('postAssessmentDiscussion')]
      || padReportDoc?.[getReportApprovedField('postAssessmentDiscussion')]
      || (padReportDoc?.outputs && Object.keys(padReportDoc.outputs).length > 0),
    );

    const leadName = patientDataModel?.clinicians?.clinicianLead?.clinicianName;

    // CYP ADHD proforma routes live under /proforma/cyp-adhd/ (mirroring the
    // adult autism convention). All four modules now have live routes.
    const moduleProps = {
      clinicalInterview: {
        clinician: patientDataModel?.clinicians?.clinicianCI?.clinicianName || leadName,
        supportingClinicians: [],
        startPath: `/patient/${patientId}/proforma/cyp-adhd/clinical-interview/patient-information`,
        reviewPath: `/patient/${patientId}/proforma/cyp-adhd/clinical-interview/risk-assessment`,
        docsAvailable: ciDocsAvailable,
      },
      semiStructuredInterview: {
        clinician: patientDataModel?.clinicians?.clinicianSSI?.clinicianName || leadName,
        supportingClinicians: [],
        // The SSI opens on its first phase. observationInSsi starts on the
        // Clinical Observation with the child; every other stamp (WLC and
        // TOC today) starts on the symptom walk.
        startPath: `/patient/${patientId}/proforma/cyp-adhd/semi-structured-interview/${wf.cypHasObservationInSsi ? 'observation' : 'symptoms'}`,
        reviewPath: `/patient/${patientId}/proforma/cyp-adhd/semi-structured-interview/review`,
        docsAvailable: ssiDocsAvailable,
      },
      diagnosticDecision: {
        clinician: patientDataModel?.clinicians?.clinicianDD?.clinicianName,
        supportingClinicians: patientDataModel?.clinicians?.clinicianDD?.supportingClinicianNames || [],
        startPath: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/mdt-review`,
        reviewPath: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/adhd`,
        docsAvailable: ddDocsAvailable,
      },
      postAssessmentDiscussion: {
        clinician: patientDataModel?.clinicians?.clinicianPAD?.clinicianName,
        supportingClinicians: [],
        startPath: `/patient/${patientId}/proforma/cyp-adhd/post-assessment-discussion/review-assessment`,
        reviewPath: `/patient/${patientId}/proforma/cyp-adhd/post-assessment-discussion/review-assessment`,
        docsAvailable: padDocsAvailable,
      },
    };

    const props = moduleProps[moduleKey] || {};

    // ── Dual-button mode (Preview + Start/Continue/Review), adult Autism parity.
    // Anyone with patient access can Preview (read-only, opens the proforma with
    // ?preview=true which useIsPreviewMode detects). The action button is only
    // enabled for the clinician assigned to that stage (clinicians.clinicianCI /
    // SSI / DD / PAD via canStartAppointment -> isClinicianAssignedToStage);
    // there is deliberately no Org Admin or Support override, matching the
    // Autism overview. Locked modules skip this since they have no actionable
    // path.
    const isDualButtonModule = !isLocked && !props.isDisabled && Boolean(props.startPath);

    let dualButtonProps = {};
    if (isDualButtonModule) {
      const actionPath = progress === 100
        ? (props.reviewPath || props.startPath)
        : props.startPath;
      const actionLabel = progress === 0 ? 'Start' : progress === 100 ? 'Review' : 'Continue';

      const previewPath = appendPreviewQuery(props.startPath);
      const canStart = canStartAppointment(patientDataModel, user?.uid, actionPath);

      dualButtonProps = { previewPath, actionPath, actionLabel, canStart };
    }

    // Appointment dates on every applicable card, adult-parity: the recorded
    // date, or the London day of the stage's completion as an inferred
    // fallback, plus the edit affordance. Disabled cards (PAD until it ships)
    // stay dateless exactly as adult's disabled cards do.
    let appointmentDate = null;
    let appointmentDateInferred = false;
    let onEditAppointmentDate = null;
    if (!props.isDisabled && wf.isAppointmentDatesEnabled) {
      const resolved = resolveModuleAppointmentDate(moduleKey);
      appointmentDate = resolved.date;
      appointmentDateInferred = resolved.isInferred;
      onEditAppointmentDate = () => setAppointmentDateDialogModule(resolved.shortKey);
    }

    const isAdminOrSupport = claims?.userType === 'Support' || claims?.userType === 'Org. Admin';
    const hasAnyClinician = !!(
      patientDataModel?.clinicians?.clinicianSSI?.clinicianId
      || patientDataModel?.clinicians?.clinicianLead?.clinicianId
    );
    const showAssignButton = isAdminOrSupport && !hasAnyClinician && !props.clinician;

    // Add-MDT-clinician on the Diagnostic Decision card, adult-parity
    // (PatientOverview.js) but gated on the CYP-explicit stamp
    // (cypAdhdFlow.mdtSupportingClinician — TOC, TDI-208): Admin/Support or
    // the patient's Lead Clinician can add ONE supporting clinician while
    // none is assigned. The click opens the shared ReassignCliniciansDialog
    // directly on its addDDSupporting step via PatientInfoPanel, and the
    // shared reassignClinicians backend handles the write (pathway-agnostic).
    const leadClinicianId = patientDataModel?.clinicians?.clinicianLead?.clinicianId;
    const isLeadClinician = !!leadClinicianId && user?.uid === leadClinicianId;
    const showAddSupporting = moduleKey === 'diagnosticDecision'
      && wf.cypEnableMdtSupportingClinician
      && !!leadClinicianId
      && (props.supportingClinicians || []).length === 0
      && (isAdminOrSupport || isLeadClinician);

    return (
      <div className={getAssessmentModuleCardWidthClass(config.moduleOrder.length)} key={moduleKey}>
        <AssessmentModuleCard
          title={moduleConfig.title}
          clinician={props.clinician}
          supportingClinicians={props.supportingClinicians}
          progress={progress}
          patientId={patientId}
          startPath={props.startPath}
          reviewPath={props.reviewPath}
          docsAvailable={props.docsAvailable}
          isLocked={isLocked}
          isDisabled={Boolean(props.isDisabled)}
          disabledReason={props.disabledReason || ''}
          circleNumber={moduleConfig.circleNumber}
          lockedTooltip={getModuleLockedTooltip(moduleKey)}
          showAssignClinicianButton={showAssignButton}
          onAssignClinicianClick={() => setShowAssignLeadDialog(true)}
          assignClinicianLabel="Assign Clinician"
          showAddSupportingButton={showAddSupporting}
          onAddSupportingClick={() => setRequestReassignStep('addDDSupporting')}
          appointmentDate={appointmentDate}
          appointmentDateInferred={appointmentDateInferred}
          onEditAppointmentDate={onEditAppointmentDate}
          {...dualButtonProps}
        />
      </div>
    );
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/cyp-adhd`],
  ];

  const progressMap = {
    clinicalInterview: CIProgress,
    semiStructuredInterview: SSIProgress,
    diagnosticDecision: DDProgress,
    postAssessmentDiscussion: PADProgress,
  };

  if (!patientDataModel) {
    return <div>Loading...</div>;
  }

  return (
    <Layout activePage="dashboard">
      <div className="flex items-center justify-between mb-4 animate-fadeIn">
        <Breadcrumb tupleList={breadcrumbTuples} />
      </div>

      <div className="flex-grow pl-8 pr-8 animate-fadeIn">
        <PatientInfoPanel
          patientDataModel={patientDataModel}
          patientId={patientId}
          informantData={informantData}
          refreshPatientData={() => fetchPatientData(patientId)}
          isSubclinicalService={false}
          requestReassignStep={requestReassignStep}
          onReassignStepHandled={() => setRequestReassignStep(null)}
          documentsRoute={`/patient/${patientId}/cyp-adhd/documents`}
          onManageAppointmentDates={
            wf.isAppointmentDatesEnabled
              ? () => setAppointmentDateDialogModule('all')
              : undefined
          }
          reportContent={reportContent}
        />

        <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">
          Appointments
        </div>

        <div className="w-full justify-between items-stretch gap-1 inline-flex">
          {config.moduleOrder.map((moduleKey) => (
            renderModuleCard(moduleKey, progressMap[moduleKey] || 0)
          ))}
        </div>

        {showAssignLeadDialog && (
          <AssignCliniciansDialog
            key={`assign-lead-${patientId}`}
            isOpen={showAssignLeadDialog}
            onClose={() => {
              setShowAssignLeadDialog(false);
              fetchPatientData(patientId);
            }}
            patientId={patientId}
            patientDataModel={patientDataModel}
            refreshPatientData={() => fetchPatientData(patientId)}
          />
        )}

        {/* Per-module appointment dates, the adult dialog unchanged: the
            stored shape and the audit subcollection are pathway-agnostic.
            predictRefinalisation / rerenderReports inject the CYP-specific
            helpers so the dialog can predict which CARs and the Patient Report
            will re-render, and kick those renders off fire-and-forget after
            the date save. manageReports stays empty (CYP does not use it). */}
        {wf.isAppointmentDatesEnabled && (
          <AppointmentDateDialog
            isOpen={appointmentDateDialogModule !== null}
            onClose={() => setAppointmentDateDialogModule(null)}
            patientId={patientId}
            appointmentDates={header?.appointmentDates || {}}
            inferredDates={Object.fromEntries(
              config.moduleOrder.map((moduleKey) => {
                const resolved = resolveModuleAppointmentDate(moduleKey);
                return [resolved.shortKey, resolved.isInferred ? resolved.date : null];
              }),
            )}
            moduleOrder={config.moduleOrder}
            modules={config.modules}
            refreshPatientData={() => fetchPatientData(patientId)}
            initialModule={
              appointmentDateDialogModule && appointmentDateDialogModule !== 'all'
                ? appointmentDateDialogModule
                : null
            }
            editableModules={config.moduleOrder
              .map((moduleKey) => STAGE_TO_PROGRESS_KEY[moduleKey])}
            organisation={header?.organisation}
            manageReports={{}}
            documentsRoute={`/patient/${patientId}/cyp-adhd/documents`}
            predictRefinalisation={(moduleKeys) =>
              predictCypAdhdAppointmentDateReportRefinalisation({
                reportContent: reportContent || {},
                moduleKeys,
                patientReportDoc: reportContent?.patientReport || null,
              })
            }
            rerenderReports={({ patientId: pid, moduleKeys: mks, clinicianId: cid }) =>
              rerenderAfterCypAdhdAppointmentDateChange({
                patientId: pid,
                moduleKeys: mks,
                clinicianId: cid,
              })
            }
          />
        )}

        <div className="text-gray-900 text-xl font-semibold leading-7 text-left pt-4 pb-4">
          Pre-Assessment Information
        </div>

        {/* CYP layout: the child answers nothing themselves, so there is no
            patient panel. Two equal-width cards (parent/carer and school)
            each hold that informant's SNAP-IV 26 scores and then their
            history forms, so a clinician compares like with like. */}
        <CypAdhdPreAppointmentDashboard
          patientDataModel={patientDataModel}
          informantData={informantData}
          reportContent={reportContent}
          patientId={patientId}
          refreshPatientData={() => fetchPatientData(patientId)}
        />
      </div>
    </Layout>
  );
}

export default CypAdhdPatientOverview;
